import React, { useState, useCallback, useMemo, useRef } from "react";
import { KonvaEventObject } from "konva/lib/Node";
import { Group, Line } from "react-konva";

import Polygon from "./polygon";
import { SkuSection, WithIndex } from "@/shared/api";
import { Vector2d } from "konva/lib/types";
import { pointsToFlatArray } from "@/features/sku-sections/lib/transform-coordinate";
import { Dimensions } from "@/shared";
import Konva from "konva";

interface SceneProps {
  cameraSkuSections: WithIndex<SkuSection>[] | null;
  imageDimensions: Dimensions;
  onPolygonClick: (
    polygon: WithIndex<SkuSection>,
    event: KonvaEventObject<MouseEvent>
  ) => void;
  originalImageSize: Dimensions;
  selectedPolygonId?: number | null;
  boundingBox?: {
    topLeft: Vector2d;
    bottomRight: Vector2d;
  } | null;
}

const createBoundingBoxPoints = (
  topLeft: Vector2d,
  bottomRight: Vector2d
): Vector2d[] => {
  const topRight: Vector2d = { x: bottomRight.x, y: topLeft.y };
  const bottomLeft: Vector2d = { x: topLeft.x, y: bottomRight.y };

  return [topLeft, topRight, bottomRight, bottomLeft, topLeft];
};

const SkuSectionPolygons: React.FC<SceneProps> = ({
  cameraSkuSections,
  boundingBox,
  imageDimensions,
  onPolygonClick,
  selectedPolygonId,
  originalImageSize,
}) => {
  const boundingBoxLineRef = useRef<Konva.Line>(null);
  const [hoveredPolygonIndex, setHoveredPolygonIndex] = useState<number | null>(
    null
  );

  const handlePointerOver = useCallback((index: number) => {
    boundingBoxLineRef.current?.moveToTop();
    setHoveredPolygonIndex(index);
  }, []);

  const handlePointerOut = useCallback(() => {
    boundingBoxLineRef.current?.moveToTop();
    setHoveredPolygonIndex(null);
  }, []);

  const boundingBoxPoints = useMemo(() => {
    if (!boundingBox) return null;

    const scaleX = imageDimensions.width / originalImageSize.width;
    const scaleY = imageDimensions.height / originalImageSize.height;

    const topLeft: Vector2d = {
      x: boundingBox.topLeft.x * scaleX,
      y: boundingBox.topLeft.y * scaleY,
    };

    const bottomRight: Vector2d = {
      x: boundingBox.bottomRight.x * scaleX,
      y: boundingBox.bottomRight.y * scaleY,
    };

    return createBoundingBoxPoints(topLeft, bottomRight);
  }, [boundingBox, imageDimensions, originalImageSize]);

  const boundingBoxFlatPoints = useMemo(() => {
    return boundingBoxPoints ? pointsToFlatArray(boundingBoxPoints) : null;
  }, [boundingBoxPoints]);

  const transformedPolygons = useMemo(() => {
    return cameraSkuSections?.map((polygon) => ({
      ...polygon,
      transformedPoints: polygon.polygon_points.map(([x, y]) => ({
        x: x * imageDimensions.width,
        y: y * imageDimensions.height,
      })),
    }));
  }, [cameraSkuSections, imageDimensions.width, imageDimensions.height]);

  return (
    <Group>
      {transformedPolygons?.map((polygon) => {
        const isHovered =
          selectedPolygonId === polygon.sku_section_id ||
          hoveredPolygonIndex === polygon.id;
        const isSelected = selectedPolygonId === polygon.sku_section_id;

        return (
          <Polygon
            key={polygon.id}
            points={polygon.transformedPoints}
            isHovered={isHovered}
            isSelected={isSelected}
            color={polygon.color}
            onPointerOver={() => handlePointerOver(polygon.id)}
            onPointerOut={handlePointerOut}
            onClick={(event) => onPolygonClick(polygon, event)}
          />
        );
      })}
      {boundingBoxFlatPoints && (
        <Line
          ref={boundingBoxLineRef}
          points={boundingBoxFlatPoints}
          stroke="#26FF00"
          strokeWidth={2}
          closed={false}
        />
      )}
    </Group>
  );
};

export default SkuSectionPolygons;
